import {
  IEmailInvitationSendPayload,
  IGanasocioInvitationSendPayload,
  IInvitationSendPayload,
  IReferral,
  IWhatsappInvitationSendPayload,
  InvitationSendContactType,
  InvitationSendLanguage
} from 'core/types/referral.interface';
import { UserTypes } from 'shared/types/user.model';
import { normalizeWebsiteUrl } from './website-url.utils';

interface IContactInvitationSendPayloadBase {
  id?: number;
  first_name: string;
  last_name: string;
  type: InvitationSendContactType;
  language?: InvitationSendLanguage;
  live_in_us?: boolean;
}

export function buildInvitationSendPayload(referral: IReferral, userType: UserTypes): IInvitationSendPayload {
  if (usesGanasocioForm(userType)) {
    return buildGanasocioPayload(referral);
  }

  const type = normalizeContactType(referral.type);
  const commonPayload = buildCommonPayload(referral, type);

  if (type === 'email') {
    return {
      ...commonPayload,
      type,
      email: normalizeString(referral.email)
    } as IEmailInvitationSendPayload;
  }

  return {
    ...commonPayload,
    type,
    country_code: normalizeString(referral.country_code),
    phone_number: normalizeString(referral.phone_number)
  } as IWhatsappInvitationSendPayload;
}

function buildCommonPayload(referral: IReferral, type: InvitationSendContactType): IContactInvitationSendPayloadBase {
  return {
    ...(typeof referral.id === 'number' ? { id: referral.id } : {}),
    first_name: normalizeString(referral.first_name),
    last_name: normalizeString(referral.last_name),
    type,
    ...(isInvitationLanguage(referral.language) ? { language: referral.language } : {}),
    ...(typeof referral.live_in_us === 'boolean' ? { live_in_us: referral.live_in_us } : {})
  };
}

function buildGanasocioPayload(referral: IReferral): IGanasocioInvitationSendPayload {
  const websiteLink = normalizeWebsiteUrl(referral.website_link);

  return {
    ...(typeof referral.id === 'number' ? { id: referral.id } : {}),
    first_name: normalizeString(referral.first_name),
    last_name: normalizeString(referral.last_name),
    type: 'email',
    country_code: normalizeString(referral.country_code),
    phone_number: normalizeString(referral.phone_number),
    email: normalizeString(referral.email),
    ...(referral.org_name?.trim() ? { org_name: referral.org_name.trim() } : {}),
    ...(websiteLink ? { website_link: websiteLink } : {}),
    good_partner: normalizeString(referral.good_partner)
  };
}

function usesGanasocioForm(userType: UserTypes): boolean {
  return [UserTypes.PR, UserTypes.AY, UserTypes.CO, UserTypes.RE].includes(userType);
}

function normalizeContactType(type?: string): InvitationSendContactType {
  return type?.toLowerCase() === 'email' ? 'email' : 'whatsapp';
}

function isInvitationLanguage(language?: string): language is InvitationSendLanguage {
  return language === 'en' || language === 'es';
}

function normalizeString(value?: string): string {
  return value?.trim() || '';
}
